import React, { useState, useEffect, useMemo, useRef } from 'react';
import { fetchWithAuth } from './lib/api';
import { DAYS, DAYS_OF_WEEK } from './schedule/constants';
import { formatTimeInput, determineShiftType, formatShiftDisplay } from './schedule/utils/timeHelpers';
import { getWeekDateRange } from './schedule/utils/dateHelpers';
import ShiftDialog from './schedule/components/ShiftDialog';
import CopyWeekDialog from './schedule/components/CopyWeekDialog';
import { EditPaymentDialog, NewPaymentDialog } from './schedule/components/PaymentDialogs';
import MobileScheduleView from './schedule/components/MobileScheduleView';
import DesktopScheduleView from './schedule/components/DesktopScheduleView';
import {
  Box,
  Typography,
  Alert,
  useMediaQuery,
  useTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import SkipNextIcon from '@mui/icons-material/SkipNext';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import isoWeek from 'dayjs/plugin/isoWeek';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import 'dayjs/locale/el';
import {
  employeeMatchesPositionFilter,
  areShiftsReadyForWeek,
  getEmployeeWeekPosition,
  shiftsForWeek,
} from './lib/shiftUtils';
import { usePresetHours } from './PresetHoursContext';
import { usePositions } from './PositionsContext';
import EmployeeManagementModal from './components/EmployeeManagementModal';
import { useEmployeePayments, useCumulativePayments, useCreatePayment, useUpdatePayment, useDeletePayment } from './hooks/useEmployeePayments';
import { useShifts } from './hooks/useShifts';
import { useQueryClient } from '@tanstack/react-query';

dayjs.extend(isoWeek);
dayjs.extend(isSameOrAfter);
dayjs.extend(isSameOrBefore);
dayjs.locale('el');

export default function Schedule() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const { presetHours } = usePresetHours();
  const { positions: positionsList } = usePositions();
  const [selectedWeek, setSelectedWeek] = useState(dayjs().startOf('isoWeek'));
  const [allEmployees, setAllEmployees] = useState([]);
  const [shifts, setShifts] = useState([]);
  const fetchGenerationRef = useRef(0);
  const weekStart = useMemo(() => selectedWeek.format('YYYY-MM-DD'), [selectedWeek]);
  const weekShifts = useMemo(() => shiftsForWeek(shifts, weekStart), [shifts, weekStart]);
  const [positions, setPositions] = useState([]);
  const [selectedPosition, setSelectedPosition] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [alert, setAlert] = useState(null);
  
  // Mobile Day Selection
  const [selectedDayIndex, setSelectedDayIndex] = useState(0); // Index in DAYS array
  
  // Email Notifications
  const [notifyDialog, setNotifyDialog] = useState({ open: false, loading: false, result: null });

  const shiftNotifyPreview = useMemo(() => {
    const employeeIds = [...new Set(weekShifts.map((s) => s.employee_id))];
    const byId = Object.fromEntries(allEmployees.map((e) => [e.id, e]));
    const withEmail = [];
    const withoutEmail = [];

    for (const id of employeeIds) {
      const emp = byId[id];
      if (!emp) continue;
      const shiftCount = weekShifts.filter((s) => s.employee_id === id).length;
      const entry = { id: emp.id, name: emp.name, email: emp.email?.trim() || '', shiftCount };
      if (entry.email) withEmail.push(entry);
      else withoutEmail.push(entry);
    }

    return { withEmail, withoutEmail };
  }, [weekShifts, allEmployees]);

  const employees = useMemo(() => {
    let filtered = allEmployees;
    const shiftsReady = areShiftsReadyForWeek(shifts, weekStart, loading);

    if (selectedPosition !== '' && shiftsReady) {
      filtered = filtered.filter((emp) =>
        employeeMatchesPositionFilter(emp, shifts, selectedPosition, weekStart)
      );
    }

    if (searchTerm && searchTerm.trim() !== '') {
      const searchLower = searchTerm.toLowerCase().trim();
      filtered = filtered.filter((emp) =>
        (emp.name || '').toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [allEmployees, shifts, weekStart, loading, selectedPosition, searchTerm]);

  const getEmployeeDisplayPosition = (employee) =>
    getEmployeeWeekPosition(employee, shifts, weekStart);
  
  // Unified Employee Management Modal
  const [unifiedModal, setUnifiedModal] = useState({ open: false, emp: null, tab: 0 });
  const [editForm, setEditForm] = useState({});
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [paymentStats, setPaymentStats] = useState({ totalSalary: 0, totalPaid: 0, unpaid: 0 });
  const [paymentStatsFilter, setPaymentStatsFilter] = useState('current_week');
  const [employeeShifts, setEmployeeShifts] = useState([]);
  const [currentEmployee, setCurrentEmployee] = useState(null);
  const [historyDateFrom, setHistoryDateFrom] = useState('');
  const [historyDateTo, setHistoryDateTo] = useState('');
  const [historyGroupBy, setHistoryGroupBy] = useState('none');
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [shiftsEmployeeId, setShiftsEmployeeId] = useState(null);
  
  // React Query hooks
  const queryClient = useQueryClient();
  const weekStartStr = dayjs().startOf('isoWeek').format('YYYY-MM-DD');
  const { data: cumulativeData = [] } = useCumulativePayments(weekStartStr);
  const { data: paymentHistoryData = [], refetch: refetchPaymentHistory } = useEmployeePayments(selectedEmployeeId);
  const { data: employeeShiftsData = [], refetch: refetchShifts } = useShifts(
    shiftsEmployeeId ? { employee_id: shiftsEmployeeId } : {}
  );
  
  // React Query mutations
  const createPaymentMutation = useCreatePayment();
  const updatePaymentMutation = useUpdatePayment();
  const deletePaymentMutation = useDeletePayment();
  
  // New Payment Dialog
  const [newPaymentDialog, setNewPaymentDialog] = useState(false);
  const [newPaymentForm, setNewPaymentForm] = useState({
    payment_date: new Date().toISOString().split('T')[0],
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });
  const [unpaidWeeks, setUnpaidWeeks] = useState([]);
  const [selectedWeeks, setSelectedWeeks] = useState([]);
  
  // Edit payment state
  const [editPaymentDialog, setEditPaymentDialog] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [editPaymentForm, setEditPaymentForm] = useState({
    payment_date: '',
    amount: '',
    payment_method: 'cash',
    reference: '',
    notes: ''
  });
  
  // Dialog state - Enhanced modal
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [quickAddForm, setQuickAddForm] = useState({
    selectedDays: [],
    weekOffset: 0,
    dayTimes: {}
  });

  // Copy Week Dialog state
  const [copyWeekDialog, setCopyWeekDialog] = useState(false);
  const [copyWeekForm, setCopyWeekForm] = useState({
    weeksAhead: 1,
    skipExisting: true
  });
  const [copyingWeek, setCopyingWeek] = useState(false);
  
  // Selected employees for copy
  const [selectedForCopy, setSelectedForCopy] = useState([]);
  
  // Generate fixed schedules state
  const [generatingFixed, setGeneratingFixed] = useState(false);

  // Mobile quick add drawer
  const [mobileAddDrawer, setMobileAddDrawer] = useState({ open: false, employee: null });

  useEffect(() => {
    setShifts([]);
    fetchGenerationRef.current += 1;
    fetchData(fetchGenerationRef.current);
    fetchPositions();
  }, [selectedWeek]);

  const fetchPositions = async () => {
    try {
      const res = await fetchWithAuth('/api/positions');
      if (res.ok) {
        const data = await res.json();
        setPositions(data.map((p) => p.name));
      }
    } catch (err) {
      console.error('Error fetching positions:', err);
    }
  };

  const refreshData = () => {
    fetchGenerationRef.current += 1;
    fetchData(fetchGenerationRef.current);
  };

  const fetchData = async (fetchGeneration) => {
    setLoading(true);
    setError('');
    const requestedWeekStart = selectedWeek.format('YYYY-MM-DD');

    try {
      const employeesRes = await fetchWithAuth('/api/employees');
      const employeesData = await employeesRes.json();
      const activeEmployees = employeesData.filter((emp) => emp.active !== 0);

      if (fetchGeneration !== fetchGenerationRef.current) return;

      setAllEmployees(activeEmployees);

      const shiftsRes = await fetchWithAuth(`/api/shifts?week_start=${requestedWeekStart}`);
      const shiftsData = await shiftsRes.json();

      if (fetchGeneration !== fetchGenerationRef.current) return;

      setShifts(shiftsData);
    } catch (err) {
      if (fetchGeneration === fetchGenerationRef.current) {
        setError('Σφάλμα φόρτωσης δεδομένων');
        console.error(err);
      }
    } finally {
      if (fetchGeneration === fetchGenerationRef.current) {
        setLoading(false);
      }
    }
  };

  const getShiftForCell = (employeeId, day) => {
    return weekShifts.find(
      (s) => s.employee_id === employeeId && String(s.day) === String(day)
    );
  };

  const showAlert = (message, severity = 'info') => {
    setAlert({ message, severity });
    setTimeout(() => setAlert(null), 4000);
  };

  // Get current day value from index
  const getCurrentDayValue = () => {
    return DAYS[selectedDayIndex].value;
  };

  // Get shifts for selected day (mobile) - sorted: with shifts first, then without, alphabetically
  const getShiftsForSelectedDay = () => {
    const dayValue = getCurrentDayValue();
    const data = employees.map(employee => {
      const shift = getShiftForCell(employee.id, dayValue);
      return { employee, shift };
    });
    
    // Sort: employees with shifts first, then without shifts
    // Within each group, sort alphabetically by name
    return data.sort((a, b) => {
      // First priority: has shift vs no shift
      const aHasShift = a.shift !== null && a.shift !== undefined;
      const bHasShift = b.shift !== null && b.shift !== undefined;
      
      if (aHasShift && !bHasShift) return -1; // a has shift, b doesn't -> a first
      if (!aHasShift && bHasShift) return 1;  // b has shift, a doesn't -> b first
      
      // Second priority: alphabetical by name
      return (a.employee.name || '').localeCompare(b.employee.name || '', 'el');
    });
  };

  // Mobile: Navigate days
  const handlePreviousDay = () => {
    setSelectedDayIndex(prev => (prev === 0 ? 6 : prev - 1));
  };

  const handleNextDay = () => {
    setSelectedDayIndex(prev => (prev === 6 ? 0 : prev + 1));
  };

  // Mobile: Quick add shift
  const handleMobileQuickAdd = (employee) => {
    const dayValue = getCurrentDayValue();
    const existingShift = getShiftForCell(employee.id, dayValue);
    
    let defaultStart = '09:00';
    let defaultEnd = '17:00';
    
    if (presetHours && presetHours.length > 0) {
      const [start, end] = presetHours[0].split('-');
      defaultStart = start;
      defaultEnd = end;
    }
    
    // Φόρτωση ΟΛΩΝ των βαρδιών του υπαλλήλου για την εβδομάδα
    const employeeShiftsThisWeek = weekShifts.filter((s) => s.employee_id === employee.id);
    
    const dayTimes = {};
    const daysWithShifts = [];
    
    DAYS_OF_WEEK.forEach(d => {
      const shiftForDay = employeeShiftsThisWeek.find(s => String(s.day) === String(d.value));
      if (shiftForDay) {
        dayTimes[d.value] = {
          startTime: shiftForDay.start_time,
          endTime: shiftForDay.end_time
        };
        daysWithShifts.push(d.value);
      } else {
        dayTimes[d.value] = {
          startTime: defaultStart,
          endTime: defaultEnd
        };
      }
    });
    
    // Προ-επιλογή: όλες οι μέρες με υπάρχουσες βάρδιες + η μέρα που κλικάραμε
    const initialSelected = [...new Set([...daysWithShifts, dayValue])];
    
    setSelectedCell({ employee, day: dayValue, existingShift });
    setQuickAddForm({
      selectedDays: initialSelected,
      weekOffset: 0,
      dayTimes: dayTimes
    });
    setOpenDialog(true);
  };

  const handleCellClick = (employee, day) => {
    let defaultStart = '09:00';
    let defaultEnd = '17:00';
    
    if (presetHours && presetHours.length > 0) {
      const [start, end] = presetHours[0].split('-');
      defaultStart = start;
      defaultEnd = end;
    }
    
    const existingShift = getShiftForCell(employee.id, day);
    
    // Φόρτωση ΟΛΩΝ των βαρδιών του υπαλλήλου για την εβδομάδα
    const employeeShiftsThisWeek = weekShifts.filter((s) => s.employee_id === employee.id);
    
    const dayTimes = {};
    const daysWithShifts = [];
    
    DAYS_OF_WEEK.forEach(d => {
      const shiftForDay = employeeShiftsThisWeek.find(s => String(s.day) === String(d.value));
      if (shiftForDay) {
        dayTimes[d.value] = {
          startTime: shiftForDay.start_time,
          endTime: shiftForDay.end_time
        };
        daysWithShifts.push(d.value);
      } else {
        dayTimes[d.value] = {
          startTime: defaultStart,
          endTime: defaultEnd
        };
      }
    });
    
    // Προ-επιλογή: όλες οι μέρες με υπάρχουσες βάρδιες + η μέρα που κλικάραμε
    const initialSelected = [...new Set([...daysWithShifts, day])];
    
    setSelectedCell({ employee, day, existingShift });
    setQuickAddForm({
      selectedDays: initialSelected,
      weekOffset: 0,
      dayTimes: dayTimes
    });
    setOpenDialog(true);
  };

  const handleDayToggle = (day) => {
    const { selectedDays } = quickAddForm;
    if (selectedDays.includes(day)) {
      setQuickAddForm({
        ...quickAddForm,
        selectedDays: selectedDays.filter(d => d !== day)
      });
    } else {
      setQuickAddForm({
        ...quickAddForm,
        selectedDays: [...selectedDays, day]
      });
    }
  };

  const handleSaveShift = async () => {
    if (!selectedCell) return;

    const { employee } = selectedCell;
    const { selectedDays, dayTimes, weekOffset } = quickAddForm;

    // Validate selected days have times
    for (const day of selectedDays) {
      const times = dayTimes[day];
      if (!times?.startTime || !times?.endTime) {
        showAlert('Συμπληρώστε ώρες έναρξης και λήξης για όλες τις επιλεγμένες ημέρες', 'warning');
        return;
      }
    }

    try {
      const weekStart = dayjs(selectedWeek).add(weekOffset * 7, 'day').format('YYYY-MM-DD');
      
      // Βρες ποιες μέρες έχουν ήδη βάρδια
      const employeeShiftsThisWeek = weekShifts.filter((s) => s.employee_id === employee.id);
      const existingDays = employeeShiftsThisWeek.map(s => s.day);
      
      // Μέρες που πρέπει να διαγραφούν (είχαν βάρδια αλλά ξετικάρονται)
      const daysToDelete = existingDays.filter(d => !selectedDays.includes(d));
      
      // Μέρες που πρέπει να προστεθούν/ενημερωθούν
      const daysToUpsert = selectedDays;

      let deleteCount = 0;
      let successCount = 0;
      let updateCount = 0;
      let errorCount = 0;

      // 1. Διαγραφή βαρδιών για τις ξετικαρισμένες μέρες
      for (const dayToDelete of daysToDelete) {
        const shiftToDelete = employeeShiftsThisWeek.find(s => s.day === dayToDelete);
        if (shiftToDelete) {
          try {
            const response = await fetchWithAuth(`/api/shifts/${shiftToDelete.id}`, {
              method: 'DELETE'
            });
            if (response.ok) {
              deleteCount++;
            } else {
              errorCount++;
            }
          } catch (err) {
            errorCount++;
          }
        }
      }

      // 2. Προσθήκη/Ενημέρωση βαρδιών για τις τικαρισμένες μέρες
      for (const dayOfWeek of daysToUpsert) {
        const times = dayTimes[dayOfWeek];
        const shiftType = determineShiftType(times.startTime);
        
        const shiftData = {
          day: dayOfWeek,
          employee_id: employee.id,
          start_time: times.startTime,
          end_time: times.endTime,
          week_start: weekStart,
          shift_type: shiftType
        };

        const existingShift = employeeShiftsThisWeek.find(s => s.day === dayOfWeek);
        
        try {
          let response;
          if (existingShift) {
            // Update existing shift
            response = await fetchWithAuth(`/api/shifts/${existingShift.id}`, {
              method: 'PUT',
              body: JSON.stringify(shiftData)
            });
            if (response.ok) updateCount++;
          } else {
            // Create new shift
            response = await fetchWithAuth('/api/shifts', {
              method: 'POST',
              body: JSON.stringify(shiftData)
            });
            if (response.ok) successCount++;
          }

          if (!response.ok) {
            const data = await response.json();
            if (!(response.status === 409 || data.error?.includes('UNIQUE constraint'))) {
              errorCount++;
            }
          }
        } catch (err) {
          errorCount++;
        }
      }

      // Build result message
      const messages = [];
      if (successCount > 0) messages.push(`${successCount} νέες`);
      if (updateCount > 0) messages.push(`${updateCount} ενημερώθηκαν`);
      if (deleteCount > 0) messages.push(`${deleteCount} διαγράφηκαν`);

      if (messages.length > 0 && errorCount === 0) {
        showAlert(`Βάρδιες: ${messages.join(', ')}`, 'success');
        setOpenDialog(false);
        refreshData();
      } else if (messages.length > 0) {
        showAlert(`Βάρδιες: ${messages.join(', ')}. Σφάλματα: ${errorCount}`, 'warning');
        setOpenDialog(false);
        refreshData();
      } else if (errorCount > 0) {
        showAlert('Σφάλμα αποθήκευσης βάρδιας', 'error');
      } else {
        showAlert('Δεν υπήρχαν αλλαγές', 'info');
        setOpenDialog(false);
      }
    } catch (error) {
      showAlert('Σφάλμα αποθήκευσης βάρδιας', 'error');
    }
  };

  const handleDeleteShift = async (shiftId, employeeName) => {
    try {
      const shift = shifts.find(s => s.id === shiftId);
      if (!shift) {
        showAlert('Η βάρδια δεν βρέθηκε', 'error');
        return;
      }

      const weekStart = shift.week_start;
      const paymentsResponse = await fetchWithAuth(
        `/api/employee-payments/allocations/${shift.employee_id}/${weekStart}`
      );
      
      if (paymentsResponse.ok) {
        const payments = await paymentsResponse.json();
        
        if (payments.length > 0) {
          const totalPaid = payments.reduce((sum, p) => sum + p.allocated_amount, 0);
          
          const confirmMessage = `⚠️ ΠΡΟΣΟΧΗ!\n\n` +
            `Ο/Η ${employeeName} έχει πληρωθεί €${totalPaid.toFixed(2)} για αυτή την εβδομάδα.\n\n` +
            `Αν διαγράψεις αυτή τη βάρδια, θα διαγραφούν ΑΥΤΟΜΑΤΑ και όλες οι πληρωμές της εβδομάδας.\n\n` +
            `Θέλεις να συνεχίσεις;`;
          
          if (!window.confirm(confirmMessage)) {
            return;
          }
          
          for (const payment of payments) {
            await fetchWithAuth(`/api/employee-payments/${payment.payment_id}`, {
              method: 'DELETE'
            });
          }
        }
      }

      const response = await fetchWithAuth(`/api/shifts/${shiftId}`, {
        method: 'DELETE',
      });

      if (!response.ok) throw new Error('Failed to delete shift');

      showAlert('Η βάρδια διαγράφηκε επιτυχώς', 'success');
      refreshData();
    } catch (error) {
      showAlert('Σφάλμα διαγραφής βάρδιας', 'error');
    }
  };

  // Generate Fixed Schedules
  const handleGenerateFixedSchedules = async () => {
    setGeneratingFixed(true);
    try {
      const response = await fetchWithAuth('/api/fixed-schedules/generate', {
        method: 'POST',
        body: JSON.stringify({
          week_start: selectedWeek.format('YYYY-MM-DD')
        })
      });
      
      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Σφάλμα δημιουργίας');
      }
      
      if (result.created > 0) {
        showAlert(`Δημιουργήθηκαν ${result.created} βάρδιες από σταθερά προγράμματα`, 'success');
        refreshData();
      } else if (result.skipped > 0) {
        showAlert(`Όλες οι σταθερές βάρδιες υπήρχαν ήδη (${result.skipped} παραλείφθηκαν)`, 'info');
      } else {
        showAlert('Δεν βρέθηκαν υπάλληλοι με σταθερό πρόγραμμα', 'warning');
      }
    } catch (error) {
      showAlert(`Σφάλμα: ${error.message}`, 'error');
    } finally {
      setGeneratingFixed(false);
    }
  };

  // Send Shift Notifications
  const handleOpenNotifyDialog = () => {
    setNotifyDialog({ open: true, loading: false, result: null });
  };

  const handleSendNotifications = async () => {
    setNotifyDialog(prev => ({ ...prev, loading: true }));
    try {
      const response = await fetchWithAuth('/api/shifts/notify-week', {
        method: 'POST',
        body: JSON.stringify({ week_start: selectedWeek.format('YYYY-MM-DD') })
      });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.error || 'Σφάλμα αποστολής');
      }
      setNotifyDialog(prev => ({ ...prev, loading: false, result }));
      if (result.sent > 0) {
        if (result.email_configured === false) {
          showAlert('Δοκιμαστική λειτουργία: δεν υπάρχει RESEND_API_KEY — δεν στάλθηκαν πραγματικά emails', 'warning');
        } else {
          showAlert(`Στάλθηκαν ${result.sent} ειδοποιήσεις βαρδιών`, 'success');
        }
      }
    } catch (error) {
      setNotifyDialog(prev => ({ 
        ...prev, 
        loading: false, 
        result: { success: false, error: error.message } 
      }));
      showAlert(`Σφάλμα: ${error.message}`, 'error');
    }
  };

  const handleCloseNotifyDialog = () => {
    setNotifyDialog({ open: false, loading: false, result: null });
  };

  // Copy Week Handlers
  const handleOpenCopyWeek = () => {
    setCopyWeekForm({
      weeksAhead: 1,
      selectedEmployees: [],
      skipExisting: true
    });
    setCopyWeekDialog(true);
  };

  const handleCopyWeek = async () => {
    const employeesWithShifts = employees.filter((e) => weekShifts.some((s) => s.employee_id === e.id));
    const employeesToCopy = selectedForCopy.length > 0 
      ? selectedForCopy 
      : employeesWithShifts.map(e => e.id);
    
    const shiftsToCount = weekShifts.filter((s) => employeesToCopy.includes(s.employee_id));
    
    if (shiftsToCount.length === 0) {
      showAlert('Δεν υπάρχουν βάρδιες για αντιγραφή', 'warning');
      return;
    }

    setCopyingWeek(true);
    try {
      const targetWeeks = [];
      for (let i = 1; i <= copyWeekForm.weeksAhead; i++) {
        targetWeeks.push(selectedWeek.add(i, 'week').format('YYYY-MM-DD'));
      }

      const response = await fetchWithAuth('/api/shifts/copy-week', {
        method: 'POST',
        body: JSON.stringify({
          source_week: selectedWeek.format('YYYY-MM-DD'),
          target_weeks: targetWeeks,
          employee_ids: selectedForCopy.length > 0 ? selectedForCopy : undefined,
          skip_existing: copyWeekForm.skipExisting
        })
      });

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error || 'Σφάλμα αντιγραφής');
      }

      showAlert(result.message, 'success');
      setCopyWeekDialog(false);
      setSelectedForCopy([]);
      refreshData();
    } catch (error) {
      showAlert(`Σφάλμα: ${error.message}`, 'error');
    } finally {
      setCopyingWeek(false);
    }
  };

  // Payment Stats
  const paymentStatsCalculated = useMemo(() => {
    if (!selectedEmployeeId || !currentEmployee) {
      return { totalSalary: 0, totalPaid: 0, unpaid: 0 };
    }
    
    let totalSalary = 0;
    if (employeeShiftsData && employeeShiftsData.length > 0) {
      employeeShiftsData.forEach(shift => {
        const startParts = shift.start_time.split(':');
        const endParts = shift.end_time.split(':');
        const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
        let endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
        if (endMinutes < startMinutes) {
          endMinutes += 24 * 60;
        }
        const hours = (endMinutes - startMinutes) / 60;
        
        if (hours > 0) {
          const wageRate = shift.hourly_wage || 0;
          totalSalary += hours * wageRate;
        }
      });
    }
    
    const totalPaid = paymentHistoryData.reduce((sum, payment) => sum + parseFloat(payment.amount || 0), 0);
    const unpaid = totalSalary - totalPaid;
    
    return { totalSalary: totalSalary || 0, totalPaid: totalPaid || 0, unpaid: unpaid || 0 };
  }, [selectedEmployeeId, currentEmployee, employeeShiftsData, paymentHistoryData]);

  useEffect(() => {
    setPaymentStats(paymentStatsCalculated);
  }, [paymentStatsCalculated]);

  useEffect(() => {
    if (paymentHistoryData) {
      setPaymentHistory(Array.isArray(paymentHistoryData) ? paymentHistoryData : []);
    }
  }, [paymentHistoryData]);

  useEffect(() => {
    if (employeeShiftsData) {
      setEmployeeShifts(Array.isArray(employeeShiftsData) ? employeeShiftsData : []);
    }
  }, [employeeShiftsData]);

  useEffect(() => {
    if (unifiedModal.open && unifiedModal.tab === 2 && unifiedModal.emp) {
      refetchShifts();
    }
  }, [unifiedModal.open, unifiedModal.tab, unifiedModal.emp, refetchShifts]);

  const openUnifiedModal = async (emp, tab = 0) => {
    setCurrentEmployee(emp);
    setUnifiedModal({ open: true, emp, tab });
    
    setEditForm({
      name: emp.name,
      email: emp.email || '',
      position: emp.position,
      hourly_wage: emp.hourly_wage,
      saturday_wage: emp.saturday_wage || '',
      sunday_wage: emp.sunday_wage || '',
      shift_preference: emp.shift_preference,
      max_days_per_week: emp.max_days_per_week || 7,
      available_days: emp.available_days || '0,1,2,3,4,5,6',
      shift_preferences: emp.shift_preferences || 'any',
      notes: emp.notes || ''
    });
    
    setSelectedEmployeeId(emp.id);
    setShiftsEmployeeId(emp.id);
    
    setTimeout(() => {
      refetchShifts();
    }, 100);
  };

  const handleOpenNewPayment = async () => {
    setNewPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
    setSelectedWeeks([]);
    
    const currentEmp = unifiedModal.emp;
    
    if (currentEmp && cumulativeData) {
      try {
        const employeeData = cumulativeData.find(e => e.id === currentEmp.id);
        if (employeeData && employeeData.unpaid_weeks) {
          const sortedUnpaid = employeeData.unpaid_weeks.sort((a, b) => 
            new Date(a.week_start) - new Date(b.week_start)
          );
          setUnpaidWeeks(sortedUnpaid);
        } else {
          setUnpaidWeeks([]);
        }
      } catch (error) {
        setUnpaidWeeks([]);
      }
    } else {
      setUnpaidWeeks([]);
    }
    
    setNewPaymentDialog(true);
  };

  const handleCloseNewPayment = () => {
    setNewPaymentDialog(false);
    setNewPaymentForm({
      payment_date: new Date().toISOString().split('T')[0],
      amount: '',
      payment_method: 'cash',
      reference: '',
      notes: ''
    });
  };

  const handleSaveNewPayment = async () => {
    if (!newPaymentForm.amount || parseFloat(newPaymentForm.amount) <= 0) {
      showAlert('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
      return;
    }

    const currentEmp = unifiedModal.emp;
    if (!currentEmp) {
      showAlert('Σφάλμα: Δεν βρέθηκε ο υπάλληλος', 'error');
      return;
    }

    try {
      let remainingAmount = parseFloat(newPaymentForm.amount);
      const allocations = [];
      
      // Πρώτα κατανέμουμε στις unpaidWeeks (εβδομάδες με χρέος)
      for (const week of unpaidWeeks) {
        if (remainingAmount <= 0) break;
        const weekUnpaid = week.unpaid;
        const allocationAmount = Math.min(remainingAmount, weekUnpaid);
        allocations.push({
          week_start: week.week_start,
          allocated_amount: allocationAmount
        });
        remainingAmount -= allocationAmount;
      }
      
      // Αν δεν υπάρχουν unpaidWeeks, ψάχνουμε εβδομάδες με βάρδιες
      if (allocations.length === 0 && employeeShiftsData && employeeShiftsData.length > 0) {
        // Βρίσκουμε τις μοναδικές εβδομάδες με βάρδιες
        const weeksWithShifts = [...new Set(employeeShiftsData.map(s => s.week_start))]
          .sort((a, b) => new Date(a) - new Date(b)); // Ταξινόμηση παλαιότερες πρώτα
        
        // Υπολογισμός μισθού ανά εβδομάδα
        const weekSalaries = {};
        weeksWithShifts.forEach(weekStart => {
          const weekShifts = employeeShiftsData.filter(s => s.week_start === weekStart);
          let weekSalary = 0;
          weekShifts.forEach(shift => {
            const startParts = shift.start_time.split(':');
            const endParts = shift.end_time.split(':');
            const startMinutes = parseInt(startParts[0]) * 60 + parseInt(startParts[1]);
            let endMinutes = parseInt(endParts[0]) * 60 + parseInt(endParts[1]);
            if (endMinutes <= startMinutes) endMinutes += 24 * 60;
            const hours = (endMinutes - startMinutes) / 60;
            const wage = shift.hourly_wage || currentEmp?.hourly_wage || 5;
            weekSalary += hours * wage;
          });
          weekSalaries[weekStart] = weekSalary;
        });
        
        // Κατανομή στις εβδομάδες με βάρδιες
        for (const weekStart of weeksWithShifts) {
          if (remainingAmount <= 0) break;
          const weekSalary = weekSalaries[weekStart] || 0;
          const allocationAmount = Math.min(remainingAmount, weekSalary);
          if (allocationAmount > 0) {
            allocations.push({
              week_start: weekStart,
              allocated_amount: allocationAmount
            });
            remainingAmount -= allocationAmount;
          }
        }
      }
      
      // Fallback: αν υπολείπεται ποσό ή δεν υπάρχουν καθόλου allocations
      if (remainingAmount > 0 || allocations.length === 0) {
        // Χρησιμοποιούμε την πιο πρόσφατη εβδομάδα με βάρδιες, αλλιώς την τρέχουσα
        let fallbackWeek = dayjs().startOf('isoWeek').format('YYYY-MM-DD');
        if (employeeShiftsData && employeeShiftsData.length > 0) {
          const sortedWeeks = [...new Set(employeeShiftsData.map(s => s.week_start))]
            .sort((a, b) => new Date(b) - new Date(a)); // Πιο πρόσφατη πρώτα
          fallbackWeek = sortedWeeks[0] || fallbackWeek;
        }
        
        if (allocations.length === 0) {
          allocations.push({
            week_start: fallbackWeek,
            allocated_amount: parseFloat(newPaymentForm.amount)
          });
        } else {
          // Ελέγχουμε αν η εβδομάδα υπάρχει ήδη στις allocations (αποφυγή duplicate week_start)
          const existingAlloc = allocations.find(a => a.week_start === fallbackWeek);
          if (existingAlloc) {
            existingAlloc.allocated_amount += remainingAmount;
          } else {
            allocations.push({
              week_start: fallbackWeek,
              allocated_amount: remainingAmount
            });
          }
        }
      }

      let paymentDate = newPaymentForm.payment_date;
      if (paymentDate && paymentDate.includes('T')) {
        paymentDate = paymentDate.split('T')[0];
      }
      
      const paymentData = {
        employee_id: currentEmp.id,
        payment_date: paymentDate,
        amount: parseFloat(newPaymentForm.amount),
        payment_method: newPaymentForm.payment_method || 'cash',
        reference: newPaymentForm.reference || null,
        notes: newPaymentForm.notes || null,
        allocations: allocations
      };
      
      await createPaymentMutation.mutateAsync(paymentData);
      showAlert('Η πληρωμή καταχωρήθηκε επιτυχώς', 'success');
      handleCloseNewPayment();
      if (currentEmp) {
        refetchPaymentHistory();
      }
    } catch (error) {
      showAlert(`Σφάλμα κατά την αποθήκευση της πληρωμής: ${error.message}`, 'error');
    }
  };

  const handleEditChange = (e) => {
    setEditForm({ ...editForm, [e.target.name]: e.target.value });
  };

  const handleEditSave = async () => {
    const emp = unifiedModal.emp;
    if (!emp) return;

    try {
      const response = await fetchWithAuth(`/api/employees/${emp.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          name: editForm.name,
          email: editForm.email || null,
          position: editForm.position,
          hourly_wage: parseFloat(editForm.hourly_wage),
          saturday_wage: editForm.saturday_wage ? parseFloat(editForm.saturday_wage) : null,
          sunday_wage: editForm.sunday_wage ? parseFloat(editForm.sunday_wage) : null,
          shift_preference: editForm.shift_preference
        })
      });

      if (response.ok) {
        showAlert('Οι αλλαγές αποθηκεύτηκαν επιτυχώς', 'success');
        setUnifiedModal({ open: false, emp: null, tab: 0 });
        refreshData();
      } else {
        showAlert('Σφάλμα αποθήκευσης', 'error');
      }
    } catch (error) {
      showAlert('Σφάλμα αποθήκευσης', 'error');
    }
  };

  const handlePreferencesSave = async () => {
    const emp = unifiedModal.emp;
    if (!emp) return;

    try {
      const response = await fetchWithAuth(`/api/employees/${emp.id}`, {
        method: 'PUT',
        body: JSON.stringify({
          max_days_per_week: parseInt(editForm.max_days_per_week),
          available_days: editForm.available_days,
          shift_preferences: editForm.shift_preferences,
          notes: editForm.notes
        })
      });

      if (response.ok) {
        showAlert('Οι προτιμήσεις αποθηκεύτηκαν επιτυχώς', 'success');
        setUnifiedModal({ open: false, emp: null, tab: 0 });
        refreshData();
      } else {
        showAlert('Σφάλμα αποθήκευσης', 'error');
      }
    } catch (error) {
      showAlert('Σφάλμα αποθήκευσης', 'error');
    }
  };

  const handleOpenEditPayment = (payment) => {
    setEditingPayment(payment);
    setEditPaymentForm({
      payment_date: payment.payment_date,
      amount: payment.amount,
      payment_method: payment.payment_method,
      reference: payment.reference || '',
      notes: payment.notes || ''
    });
    setEditPaymentDialog(true);
  };

  const handleCloseEditPayment = () => {
    setEditPaymentDialog(false);
    setEditingPayment(null);
  };

  const handleSaveEditPayment = async () => {
    if (!editPaymentForm.amount || parseFloat(editPaymentForm.amount) <= 0) {
      showAlert('Το ποσό πρέπει να είναι μεγαλύτερο από 0', 'warning');
      return;
    }

    if (!editingPayment) {
      showAlert('Δεν υπάρχει πληρωμή προς επεξεργασία', 'error');
      return;
    }

    try {
      await updatePaymentMutation.mutateAsync({
        id: editingPayment.id,
        data: {
          ...editPaymentForm,
          amount: parseFloat(editPaymentForm.amount),
          employee_id: editingPayment.employee_id
        }
      });
      showAlert('Η πληρωμή ενημερώθηκε επιτυχώς!', 'success');
      handleCloseEditPayment();
      refetchPaymentHistory();
    } catch (error) {
      showAlert(`Σφάλμα ενημέρωσης πληρωμής: ${error.message}`, 'error');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Είστε σίγουροι ότι θέλετε να διαγράψετε αυτή την πληρωμή;')) {
      return;
    }

    try {
      await deletePaymentMutation.mutateAsync(paymentId);
      showAlert('Η πληρωμή διαγράφηκε επιτυχώς!', 'success');
      refetchPaymentHistory();
    } catch (error) {
      showAlert(`Σφάλμα διαγραφής πληρωμής: ${error.message}`, 'error');
    }
  };

  // Get date for selected day
  const getSelectedDayDate = () => {
    const dayValue = getCurrentDayValue();
    // Convert day value to day offset from Monday
    const dayOffset = dayValue === 0 ? 6 : dayValue - 1;
    return selectedWeek.add(dayOffset, 'day');
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="el">
      <Box sx={{ p: { xs: 1, md: 3 } }}>
        {!isMobile && (
          <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            Πρόγραμμα
          </Typography>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        )}

        {/* Conditional render based on screen size */}
        {isMobile ? (
          <MobileScheduleView
            selectedWeek={selectedWeek}
            shifts={weekShifts}
            positions={positions}
            dayData={getShiftsForSelectedDay()}
            currentDay={DAYS[selectedDayIndex]}
            dayDate={getSelectedDayDate()}
            selectedDayIndex={selectedDayIndex}
            searchTerm={searchTerm}
            selectedPosition={selectedPosition}
            generatingFixed={generatingFixed}
            onNotify={handleOpenNotifyDialog}
            onWeekChange={(offset) => setSelectedWeek(prev => prev.add(offset, 'week'))}
            onDayChange={setSelectedDayIndex}
            onSearchChange={setSearchTerm}
            onPositionChange={setSelectedPosition}
            onOpenCopyWeek={handleOpenCopyWeek}
            onGenerateFixed={handleGenerateFixedSchedules}
            onOpenEmployee={(emp) => openUnifiedModal(emp, 0)}
            onQuickAdd={handleMobileQuickAdd}
            onDeleteShift={handleDeleteShift}
            getEmployeeDisplayPosition={getEmployeeDisplayPosition}
          />
        ) : (
          <DesktopScheduleView
            selectedWeek={selectedWeek}
            employees={employees}
            shifts={weekShifts}
            positions={positions}
            searchTerm={searchTerm}
            selectedPosition={selectedPosition}
            selectedForCopy={selectedForCopy}
            generatingFixed={generatingFixed}
            onWeekChange={setSelectedWeek}
            onSearchChange={setSearchTerm}
            onPositionChange={setSelectedPosition}
            onSelectedForCopyChange={setSelectedForCopy}
            onOpenCopyWeek={handleOpenCopyWeek}
            onGenerateFixed={handleGenerateFixedSchedules}
            onNotify={handleOpenNotifyDialog}
            onOpenEmployee={(emp) => openUnifiedModal(emp, 0)}
            onCellClick={handleCellClick}
            onDeleteShift={handleDeleteShift}
            getShiftForCell={getShiftForCell}
            getEmployeeDisplayPosition={getEmployeeDisplayPosition}
          />
        )}

        {/* Add/Edit Shift Dialog */}
        <ShiftDialog
          open={openDialog} 
          onClose={() => setOpenDialog(false)} 
          isMobile={isMobile}
          selectedCell={selectedCell}
          quickAddForm={quickAddForm}
          setQuickAddForm={setQuickAddForm}
          selectedWeek={selectedWeek}
          presetHours={presetHours}
          shifts={weekShifts}
          onDayToggle={handleDayToggle}
          onSave={handleSaveShift}
        />

        {/* Employee Management Modal */}
        <EmployeeManagementModal
          open={unifiedModal.open}
          employee={unifiedModal.emp}
          tab={unifiedModal.tab}
          onClose={() => setUnifiedModal({ open: false, emp: null, tab: 0 })}
          onTabChange={(newTab) => setUnifiedModal({ ...unifiedModal, tab: newTab })}
          positions={positionsList}
          editForm={editForm}
          onEditFormChange={handleEditChange}
          onEditFormSet={setEditForm}
          onEditSave={handleEditSave}
          onPreferencesSave={handlePreferencesSave}
          paymentStats={paymentStats}
          paymentHistory={paymentHistory}
          employeeShifts={employeeShiftsData || employeeShifts}
          onOpenNewPayment={handleOpenNewPayment}
          onOpenEditPayment={handleOpenEditPayment}
          onDeletePayment={handleDeletePayment}
          showFixedSchedule={false}
          onRefetchPaymentHistory={refetchPaymentHistory}
          onRefetchShifts={refetchShifts}
        />

        {/* Edit Payment Dialog */}
        <EditPaymentDialog
          open={editPaymentDialog}
          onClose={handleCloseEditPayment}
          editPaymentForm={editPaymentForm}
          setEditPaymentForm={setEditPaymentForm}
          onSave={handleSaveEditPayment}
        />

        {/* New Payment Dialog */}
        <NewPaymentDialog
          open={newPaymentDialog}
          onClose={handleCloseNewPayment}
          employeeName={unifiedModal.emp?.name}
          newPaymentForm={newPaymentForm}
          setNewPaymentForm={setNewPaymentForm}
          unpaidWeeks={unpaidWeeks}
          onSave={handleSaveNewPayment}
        />

        {/* Copy Week Dialog */}
        <CopyWeekDialog
          open={copyWeekDialog}
          onClose={() => setCopyWeekDialog(false)}
          selectedWeek={selectedWeek}
          shifts={shifts}
          selectedForCopy={selectedForCopy}
          copyWeekForm={copyWeekForm}
          setCopyWeekForm={setCopyWeekForm}
          copyingWeek={copyingWeek}
          onCopy={handleCopyWeek}
        />

        {/* Shift Notification Dialog */}
        <Dialog 
          open={notifyDialog.open} 
          onClose={handleCloseNotifyDialog}
          maxWidth="sm" 
          fullWidth
        >
          <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <EmailIcon color="primary" />
            Αποστολή Ειδοποιήσεων Βαρδιών
          </DialogTitle>
          <DialogContent>
            {!notifyDialog.result && !notifyDialog.loading && (
              <Box sx={{ py: 1 }}>
                <Typography variant="body1" gutterBottom>
                  Θα σταλεί email στους υπαλλήλους με βάρδιες αυτή την εβδομάδα και καταχωρημένο email.
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Εβδομάδα:{' '}
                  <strong>
                    {selectedWeek.format('DD/MM')} - {selectedWeek.clone().add(6, 'day').format('DD/MM/YYYY')}
                  </strong>
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                  Βάρδιες: <strong>{weekShifts.length}</strong> · Θα σταλούν:{' '}
                  <strong>{shiftNotifyPreview.withEmail.length}</strong> email
                </Typography>

                {shiftNotifyPreview.withEmail.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                      Παραλήπτες
                    </Typography>
                    <List dense sx={{ mb: 2, bgcolor: 'rgba(34,197,94,0.06)', borderRadius: 2 }}>
                      {shiftNotifyPreview.withEmail.map((emp) => (
                        <ListItem key={emp.id} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <CheckCircleIcon color="success" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={emp.name}
                            secondary={`${emp.email} · ${emp.shiftCount} βάρδι${emp.shiftCount === 1 ? 'α' : 'ες'}`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                {shiftNotifyPreview.withoutEmail.length > 0 && (
                  <>
                    <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600, color: 'warning.main' }}>
                      Χωρίς email (θα παραλειφθούν)
                    </Typography>
                    <List dense sx={{ mb: 2, bgcolor: 'rgba(245,158,11,0.08)', borderRadius: 2 }}>
                      {shiftNotifyPreview.withoutEmail.map((emp) => (
                        <ListItem key={emp.id} sx={{ py: 0.5 }}>
                          <ListItemIcon sx={{ minWidth: 32 }}>
                            <SkipNextIcon color="warning" fontSize="small" />
                          </ListItemIcon>
                          <ListItemText
                            primary={emp.name}
                            secondary={`${emp.shiftCount} βάρδι${emp.shiftCount === 1 ? 'α' : 'ες'} — πρόσθεσε email από Προσωπικό`}
                          />
                        </ListItem>
                      ))}
                    </List>
                  </>
                )}

                {shiftNotifyPreview.withEmail.length === 0 && weekShifts.length > 0 && (
                  <Alert severity="warning">
                    Κανένας υπάλληλος με βάρδιες δεν έχει email. Πρόσθεσε email από το μενού Προσωπικό.
                  </Alert>
                )}
              </Box>
            )}
            {notifyDialog.loading && (
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 4 }}>
                <CircularProgress sx={{ mb: 2 }} />
                <Typography>Αποστολή ειδοποιήσεων...</Typography>
              </Box>
            )}
            {notifyDialog.result && (
              <Box sx={{ py: 1 }}>
                {notifyDialog.result.error ? (
                  <Alert severity="error" sx={{ mb: 2 }}>
                    {notifyDialog.result.error}
                  </Alert>
                ) : (
                  <>
                    <Alert severity="success" sx={{ mb: 2 }}>
                      {notifyDialog.result.message}
                    </Alert>
                    {notifyDialog.result.email_configured === false && (
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Το backend δεν έχει RESEND_API_KEY — τα emails καταγράφηκαν μόνο στο log, δεν στάλθηκαν.
                      </Alert>
                    )}
                    {notifyDialog.result.details && (
                      <List dense>
                        {notifyDialog.result.details.map((detail, idx) => (
                          <ListItem key={idx}>
                            <ListItemIcon sx={{ minWidth: 36 }}>
                              {detail.status === 'sent' && <CheckCircleIcon color="success" fontSize="small" />}
                              {detail.status === 'skipped' && <SkipNextIcon color="warning" fontSize="small" />}
                              {detail.status === 'error' && <ErrorIcon color="error" fontSize="small" />}
                            </ListItemIcon>
                            <ListItemText 
                              primary={detail.name}
                              secondary={
                                detail.status === 'sent' ? `Στάλθηκε στο ${detail.email}` :
                                detail.status === 'skipped' ? detail.reason :
                                `Σφάλμα: ${detail.reason}`
                              }
                            />
                          </ListItem>
                        ))}
                      </List>
                    )}
                  </>
                )}
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            {notifyDialog.result ? (
              <Button onClick={handleCloseNotifyDialog}>Κλείσιμο</Button>
            ) : (
              <>
                <Button onClick={handleCloseNotifyDialog} disabled={notifyDialog.loading}>
                  Άκυρο
                </Button>
                <Button 
                  onClick={handleSendNotifications}
                  variant="contained"
                  disabled={
                    notifyDialog.loading ||
                    weekShifts.length === 0 ||
                    shiftNotifyPreview.withEmail.length === 0
                  }
                  startIcon={<EmailIcon />}
                >
                  Αποστολή ({shiftNotifyPreview.withEmail.length})
                </Button>
              </>
            )}
          </DialogActions>
        </Dialog>

        {/* Alert */}
        {alert && (
          <Alert 
            severity={alert.severity} 
            sx={{ 
              position: 'fixed', 
              top: 16, 
              right: 16, 
              zIndex: 9999,
              boxShadow: 3
            }}
          >
            {alert.message}
          </Alert>
        )}
      </Box>
    </LocalizationProvider>
  );
}

